import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tabs,
  Table,
  Alert,
  Typography
} from 'antd';
import {
  LinkOutlined,
  EditOutlined,
  ReloadOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  SendOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { TabPane } = Tabs;
const { Text, Paragraph } = Typography;

// Create axios instance with JWT authentication for merchant
const merchantApi = axios.create({
  baseURL: '/api/merchant',
  timeout: 30000,
});

// Request interceptor to add JWT token
merchantApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('merchant_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
merchantApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('merchant_access_token');
      localStorage.removeItem('merchant_refresh_token');
      localStorage.removeItem('merchant_user');
      window.location.href = '/merchant/login';
    }
    return Promise.reject(error);
  }
);

const MerchantCallbacks = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [callbackLogs, setCallbackLogs] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [form] = Form.useForm();
  const [logsPagination, setLogsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [resendLoading, setResendLoading] = useState(null);

  // Get merchant info from localStorage
  const [merchantData, setMerchantData] = useState(null);
  const [memberId, setMemberId] = useState(null);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('merchant_user');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setMerchantData(parsed);
        setMemberId(parsed.member_id);
      } else {
        // For development/testing - use test member ID
        setMemberId('MBR000007');
      }
    } catch (error) {
      console.error('Error parsing merchant data:', error);
      // Fallback to test member ID
      setMemberId('MBR000007');
    }
  }, []);

  useEffect(() => {
    if (memberId) {
      fetchCallbackConfig();
    }
  }, [memberId]);

  const fetchCallbackConfig = async () => {
    setLoading(true);
    try {
      const response = await merchantApi.get(`/callbacks/callback-config`);
      setConfig(response.data);
    } catch (error) {
      message.error('Gagal memuat konfigurasi callback');
      console.error('Fetch callback config error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCallbackLogs = async (page = 1, search = logSearch) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: logsPagination.pageSize
      };
      // Dicari di transaction_id maupun isi payload, sehingga ref ID
      // (external_id) di dalam callback batch ADV-... ikut ketemu.
      if (search) params.search = search;

      const response = await merchantApi.get(`/callbacks/callback-logs`, {
        params
      });
      setCallbackLogs(response.data.callback_logs);
      setLogsPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.pagination.totalItems
      }));
    } catch (error) {
      message.error('Gagal memuat log callback');
      console.error('Fetch callback logs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    form.setFieldsValue({
      ...config,
      callback_enabled: config.callback_enabled === 1,
      use_signature: config.use_signature === 1,
      email_notifications: config.email_notifications === 1,
      sms_notifications: config.sms_notifications === 1
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const response = await merchantApi.put(`/callbacks/callback-config`, values);
      message.success('Konfigurasi callback berhasil diperbarui');

      // Show new callback secret if generated
      if (response.data.callback_secret) {
        Modal.info({
          title: 'Callback Secret Baru',
          content: (
            <div>
              <p>Secret baru telah dibuat untuk signature verification:</p>
              <Input.TextArea
                value={response.data.callback_secret}
                readOnly
                autoSize={{ minRows: 2 }}
                style={{ fontFamily: 'monospace' }}
              />
              <p style={{ marginTop: 10, color: '#ff4d4f' }}>
                ⚠️ Simpan secret ini dengan aman! Anda tidak akan melihatnya lagi.
              </p>
            </div>
          ),
          width: 600
        });
      }

      setModalVisible(false);
      fetchCallbackConfig();
    } catch (error) {
      message.error('Gagal memperbarui konfigurasi callback');
      console.error('Update callback config error:', error);
    }
  };

  const handleViewLogs = () => {
    fetchCallbackLogs();
    setLogsModalVisible(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Berhasil disalin!');
  };

  const handleResendCallback = async (record) => {
    setResendLoading(record.id);
    try {
      const url = record.transaction_id?.startsWith('ADV-')
        ? `/callbacks/callback-logs/${record.id}/resend`
        : `/callbacks/transactions/${record.transaction_id}/resend-callback`;
      const res = await merchantApi.post(url);
      if (res.data.success) {
        message.success(`Callback berhasil dikirim (HTTP ${res.data.http_status})`);
      } else {
        message.warning(`Callback gagal (HTTP ${res.data.http_status}): ${res.data.response}`);
      }
      fetchCallbackLogs(logsPagination.current);
    } catch (err) {
      message.error(err.response?.data?.error || 'Gagal mengirim callback');
    } finally {
      setResendLoading(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'orange';
      case 'retrying':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return 'Berhasil';
      case 'failed':
        return 'Gagal';
      case 'pending':
        return 'Pending';
      case 'retrying':
        return 'Mencoba Ulang';
      default:
        return status;
    }
  };

  const logColumns = [
    {
      title: 'Waktu',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 160,
      render: (date) => new Date(date).toLocaleString('id-ID'),
    },
    {
      title: 'Tipe',
      dataIndex: 'callback_type',
      key: 'callback_type',
      width: 100,
      render: (type) => (
        <Tag color="blue">
          {type === 'payment' ? 'Payment' : type === 'settlement' ? 'Settlement' : 'Refund'}
        </Tag>
      ),
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 180,
      render: (id) => <Text copyable>{id}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Response Code',
      dataIndex: 'response_code',
      key: 'response_code',
      width: 120,
    },
    {
      title: 'Attempt',
      dataIndex: 'attempt_number',
      key: 'attempt_number',
      width: 80,
    },
    {
      title: 'Response Time (ms)',
      dataIndex: 'response_time_ms',
      key: 'response_time_ms',
      width: 140,
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 100,
      render: (_, record) => (record.callback_type === 'payment') ? (
        <Button
          type="primary"
          size="small"
          icon={<SendOutlined />}
          loading={resendLoading === record.id}
          onClick={() => handleResendCallback(record)}
        >
          Resend
        </Button>
      ) : <Text type="secondary" style={{ fontSize: 12 }}>-</Text>,
    },
  ];

  if (!memberId) {
    return (
      <div>
        <Card loading={true}>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h3>Loading Merchant Data...</h3>
            <p>Sedang memuat data merchant...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!config) {
    return (
      <div>
        <Card loading={loading}>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h3>Loading Callback Configuration...</h3>
            <p>Member ID: {memberId}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>Webhook & Callback Configuration</h1>
        <p style={{ color: '#666' }}>Kelola webhook URL dan konfigurasi callback untuk menerima notifikasi transaksi</p>
      </div>

      {/* Alert Info */}
      <Alert
        message="Informasi Webhook"
        description="Webhook digunakan untuk mengirim notifikasi real-time tentang status transaksi ke sistem Anda. Pastikan endpoint yang Anda daftarkan dapat menerima HTTP POST request."
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Callback Status"
              value={config.callback_enabled ? 'Enabled' : 'Disabled'}
              prefix={config.callback_enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              valueStyle={{ color: config.callback_enabled ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Max Retry"
              value={config.max_retry_attempts}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Retry Interval"
              value={config.retry_interval}
              suffix="sec"
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Timeout"
              value={config.timeout_seconds}
              suffix="sec"
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="config">
        <TabPane tab="Konfigurasi Webhook" key="config">
          <Card>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
              <h3>Detail Konfigurasi</h3>
              <Space>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  Edit Konfigurasi
                </Button>
                <Button
                  icon={<HistoryOutlined />}
                  onClick={handleViewLogs}
                >
                  Lihat Log
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchCallbackConfig}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            <Descriptions bordered column={1}>
              <Descriptions.Item label="Member ID">{config.member_id}</Descriptions.Item>
              <Descriptions.Item label="Member Name">{config.member_name}</Descriptions.Item>

              <Descriptions.Item label="Payment Callback URL">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text code style={{ flex: 1 }}>{config.payment_callback_url || 'Belum dikonfigurasi'}</Text>
                  {config.payment_callback_url && (
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(config.payment_callback_url)}
                    />
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Settlement Callback URL">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text code style={{ flex: 1 }}>{config.settlement_callback_url || 'Belum dikonfigurasi'}</Text>
                  {config.settlement_callback_url && (
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(config.settlement_callback_url)}
                    />
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Refund Callback URL">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text code style={{ flex: 1 }}>{config.refund_callback_url || 'Belum dikonfigurasi'}</Text>
                  {config.refund_callback_url && (
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(config.refund_callback_url)}
                    />
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Callback Secret">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text code style={{ flex: 1 }}>
                    {config.callback_secret ? (showSecret ? config.callback_secret : '••••••••••••••••') : 'Tidak ada'}
                  </Text>
                  {config.callback_secret && (
                    <Space>
                      <Button
                        size="small"
                        icon={showSecret ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setShowSecret(!showSecret)}
                      />
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(config.callback_secret)}
                      />
                    </Space>
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                <Tag color={config.callback_enabled ? 'green' : 'red'}>
                  {config.callback_enabled ? 'Enabled' : 'Disabled'}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Use Signature Verification">
                <Tag color={config.use_signature ? 'green' : 'red'}>
                  {config.use_signature ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Pengaturan Retry">
                Max: {config.max_retry_attempts} attempts, Interval: {config.retry_interval}s, Timeout: {config.timeout_seconds}s
              </Descriptions.Item>

              <Descriptions.Item label="Notifikasi">
                Email: <Tag color={config.email_notifications ? 'green' : 'red'}>
                  {config.email_notifications ? 'On' : 'Off'}
                </Tag>
                {' '}
                SMS: <Tag color={config.sms_notifications ? 'green' : 'red'}>
                  {config.sms_notifications ? 'On' : 'Off'}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Last Updated">
                {new Date(config.updated_at).toLocaleString('id-ID')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>

        <TabPane tab="Format Payload" key="payload">
          <Card title="Contoh Payload Webhook">
            <div style={{ marginBottom: 16 }}>
              <h4>Payment Notification</h4>
              <Input.TextArea
                readOnly
                value={JSON.stringify({
                  event_type: "payment.success",
                  transaction_id: "TRX1234567890",
                  merchant_order_id: "ORDER123",
                  amount: 100000,
                  fee: 2500,
                  net_amount: 97500,
                  currency: "IDR",
                  payment_method: "QRIS",
                  status: "success",
                  paid_at: "2024-01-15T10:30:00Z",
                  customer: {
                    name: "John Doe",
                    email: "john@example.com"
                  },
                  signature: "abc123def456..."
                }, null, 2)}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                autoSize={{ minRows: 8 }}
              />
            </div>
          </Card>
        </TabPane>
      </Tabs>

      {/* Edit Modal */}
      <Modal
        title="Edit Konfigurasi Webhook"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Payment Callback URL"
            name="payment_callback_url"
            help="URL untuk menerima notifikasi pembayaran"
          >
            <Input placeholder="https://your-domain.com/webhook/payment" />
          </Form.Item>

          <Form.Item
            label="Settlement Callback URL"
            name="settlement_callback_url"
            help="URL untuk menerima notifikasi settlement"
          >
            <Input placeholder="https://your-domain.com/webhook/settlement" />
          </Form.Item>

          <Form.Item
            label="Refund Callback URL"
            name="refund_callback_url"
            help="URL untuk menerima notifikasi refund"
          >
            <Input placeholder="https://your-domain.com/webhook/refund" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Max Retry Attempts"
                name="max_retry_attempts"
                help="1-10 attempts"
              >
                <InputNumber min={1} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Retry Interval (seconds)"
                name="retry_interval"
                help="30-3600 seconds"
              >
                <InputNumber min={30} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Timeout (seconds)"
                name="timeout_seconds"
                help="5-120 seconds"
              >
                <InputNumber min={5} max={120} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Enable Webhook"
                name="callback_enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Use Signature Verification"
                name="use_signature"
                valuePropName="checked"
                help="Recommended untuk keamanan"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email Notifications"
                name="email_notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="SMS Notifications"
                name="sms_notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                Simpan Konfigurasi
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Logs Modal */}
      <Modal
        title="Log Webhook"
        open={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        footer={null}
        width={1200}
      >
        <Input.Search
          allowClear
          enterButton
          placeholder="Cari ID transaksi atau Ref ID"
          value={logSearch}
          onChange={(e) => setLogSearch(e.target.value)}
          onSearch={(v) => fetchCallbackLogs(1, v)}
          style={{ marginBottom: 16, maxWidth: 420 }}
        />
        <Table
          columns={logColumns}
          dataSource={callbackLogs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: logsPagination.current,
            pageSize: logsPagination.pageSize,
            total: logsPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} logs`,
            onChange: (page) => fetchCallbackLogs(page)
          }}
          scroll={{ x: 800 }}
          size="small"
        />
      </Modal>

    </div>
  );
};

export default MerchantCallbacks;